import { RefObject, useCallback, useState } from 'react';

import constants from 'constants/constants';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import moment from 'moment-timezone';
import foresightLogoFooter from 'styles/assets/png/foresight_logo_footer.png';

import useUserHook from 'hooks/useUserHook';

import { Widget } from 'components/Dashboards/Reporting/reporting.types';
import {
    CommonExportConstants,
    getUniqueProjectContracts,
} from 'components/pages/ReportPage/hooks/commonExportUtils/commonExportUtils';

export interface ExportGridToPdfSmartOptions {
    scale?: number;
    widgetSelector?: string;
    pagePaddingMm?: number;
    fileName?: string;
    onProgress?: (progress: number, stage: string) => void;
}

interface ExportReportToPdfParams {
    gridRef: RefObject<HTMLDivElement>;
    reportName?: string;
    reportWidgets?: Widget[];
    options?: ExportGridToPdfSmartOptions;
}

// Helper to convert logo to dataURL
const getLogoDataUrl = async (logo: any): Promise<string | null> => {
    try {
        if (!logo) return null;
        // If already data URL
        if (typeof logo === 'string' && logo.startsWith('data:')) return logo;
        // If it's a URL, fetch and convert
        if (typeof logo === 'string') {
            const res = await fetch(logo, { mode: 'cors' });
            const blob = await res.blob();
            return await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
            });
        }
        return null;
    } catch (e) {
        return null;
    }
};

// Convert static foresight footer logo to data URL
const getForesightLogoFooterDataUrl = async (foresightLogoFooter: any): Promise<string | null> => {
    try {
        if (!foresightLogoFooter) return null;
        const res = await fetch(foresightLogoFooter as unknown as string, { mode: 'cors' });
        const blob = await res.blob();
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
        });
    } catch (e) {
        return null;
    }
};

// Create cover page for PDF
const createCoverPage = (
    pdf: jsPDF,
    reportName: string,
    reportWidgets: Widget[],
    logoDataUrl: string | null,
    foresightLogoFooterDataUrl: string | null,
) => {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Draw line above report name
    const reportNameX = 10;
    const reportNameY = 28;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(42);

    // Truncate report name if too long (leave space for logo on right)
    const logoSize = 20;
    const logoMargin = 10;
    const maxReportNameWidth = pageWidth - reportNameX - logoSize - logoMargin - 15; // Extra buffer

    pdf.setDrawColor(44, 62, 80);
    pdf.setLineWidth(0.5);
    const lineExtension = 2; // Extend line a bit beyond text on both sides
    const lineAboveReportNameY = reportNameY - 14;
    const lineAboveReportNameXStart = reportNameX - lineExtension;
    const lineAboveReportNameXEnd = 100;
    pdf.line(lineAboveReportNameXStart, lineAboveReportNameY, lineAboveReportNameXEnd, lineAboveReportNameY);

    // Draw report name below the line
    pdf.setTextColor(CommonExportConstants.DARK_BLUE_COLOR);
    pdf.text(reportName, reportNameX, reportNameY, {
        maxWidth: maxReportNameWidth,
    });

    // Client logo at top right
    if (logoDataUrl) {
        const logoSize = 20;
        const logoX = pageWidth - logoSize - 10;
        const logoY = 12;
        pdf.addImage(logoDataUrl, 'PNG', logoX, logoY, logoSize, logoSize);
    }

    // Project-contract combinations
    const projectContractsX = reportNameX + 5;
    const projectContracts = getUniqueProjectContracts(reportWidgets);

    if (projectContracts.length > 0) {
        const displayCount = Math.min(2, projectContracts.length);
        const itemSpacing = 25; // Gap between project items
        const moreTextSpacing = 20; // Smaller gap before "& n more" text

        // Always position near logo, anchored to bottom (higher position)
        const bottomAnchorY = pageHeight - 55;

        // Calculate total items including "& n more" if applicable
        const hasMoreText = projectContracts.length > 2;

        // Calculate starting Y by subtracting space needed for items above
        // For "& n more", use smaller spacing
        const totalSpacing = hasMoreText
            ? (displayCount - 1) * itemSpacing + moreTextSpacing
            : (displayCount - 1) * itemSpacing;
        let yPosition = bottomAnchorY - totalSpacing;

        for (let i = 0; i < displayCount; i++) {
            const { project, contract } = projectContracts[i];
            let text = `${project}: ${contract}`;

            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(48);
            pdf.setTextColor(CommonExportConstants.DARK_BLUE_COLOR);

            // Truncate text if too long (max width considering margins)
            const maxWidth = pageWidth - projectContractsX - 20; // Leave margin on right
            let textWidth = pdf.getTextWidth(text);

            if (textWidth > maxWidth) {
                // Truncate and add ellipsis
                while (textWidth > maxWidth && text.length > 3) {
                    text = text.slice(0, -1);
                    textWidth = pdf.getTextWidth(text + '...');
                }
                text = text + '...';
            }

            pdf.text(text, projectContractsX, yPosition);
            // Use smaller spacing before "& n more" text if this is the last project item
            if (i === displayCount - 1 && projectContracts.length > 2) {
                yPosition += moreTextSpacing;
            } else {
                yPosition += itemSpacing;
            }
        }

        // Show "And n more..." if there are more than 2
        if (projectContracts.length > 2) {
            const remainingCount = projectContracts.length - 2;
            let moreText = `And ${remainingCount} more...`;

            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(24);

            // Truncate if needed
            const maxWidth = pageWidth - projectContractsX - 20;
            let textWidth = pdf.getTextWidth(moreText);

            if (textWidth > maxWidth) {
                while (textWidth > maxWidth && moreText.length > 3) {
                    moreText = moreText.slice(0, -4) + '...';
                    textWidth = pdf.getTextWidth(moreText);
                }
            }

            pdf.text(moreText, projectContractsX, yPosition);
        }
    }

    // Bottom elements Y position (aligned)
    const bottomY = pageHeight - 20;

    // Add powered by above logo
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.text('Powered By', 10, bottomY - 11.5);

    // Foresight logo at bottom left
    if (foresightLogoFooterDataUrl) {
        const logoWidth = 64;
        const logoHeight = 10;
        const logoX = 10;
        pdf.addImage(foresightLogoFooterDataUrl, 'PNG', logoX, bottomY - logoHeight, logoWidth, logoHeight);
    }

    // Date at bottom right (aligned with Foresight logo center)
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(16);
    pdf.setTextColor(CommonExportConstants.DARK_BLUE_COLOR);
    const dateX = pageWidth - pdf.getTextWidth(formattedDate) - 10;
    // Align date with logo center (logo is 10mm tall, so center is 5mm from top)
    const dateY = bottomY - 2;
    pdf.text(formattedDate, dateX, dateY);
};

/**
 * Hook for exporting a grid report to PDF with smart page breaks that avoid cutting widgets
 */
export const useExportReportToPdf = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { logo } = useUserHook();

    const exportReportToPdf = useCallback(
        async ({ gridRef, reportName, reportWidgets = [], options = {} }: ExportReportToPdfParams): Promise<void> => {
            if (isLoading) {
                return;
            }

            setIsLoading(true);

            // Allow UI to update with loading state
            await new Promise((resolve) => setTimeout(resolve, 0));

            try {
                options.onProgress?.(5, 'Initializing export...');

                // Load logos
                options.onProgress?.(10, 'Loading logos...');
                const [logoDataUrl, foresightLogoFooterDataUrl] = await Promise.all([
                    getLogoDataUrl(logo),
                    getForesightLogoFooterDataUrl(foresightLogoFooter),
                ]);

                // Use reportName if provided and fileName not set in options
                const exportOptions = {
                    ...options,
                    fileName: options.fileName ?? (reportName ? `${reportName}.pdf` : 'report.pdf'),
                };

                await exportGridToPdfSmart(
                    gridRef,
                    exportOptions,
                    logoDataUrl,
                    foresightLogoFooterDataUrl,
                    reportName,
                    reportWidgets,
                );
                options.onProgress?.(100, 'Export complete!');
            } finally {
                setIsLoading(false);
            }
        },
        [isLoading, logo],
    );

    return {
        exportReportToPdf,
        isLoadingExportReportToPdf: isLoading,
    };
};

/**
 * Internal function that performs the actual PDF export
 * @param gridRef - Reference to the HTML container element holding the report
 * @param options - Configuration options for the export
 * @param logoDataUrl - Client logo data URL
 * @param foresightLogoFooterDataUrl - Foresight footer logo data URL
 * @param reportName - Name of the report for cover page
 * @param reportWidgets - Widget data for cover page
 */
async function exportGridToPdfSmart(
    gridRef: RefObject<HTMLDivElement>,
    options: ExportGridToPdfSmartOptions = {},
    logoDataUrl: string | null = null,
    foresightLogoFooterDataUrl: string | null = null,
    reportName: string = 'Report',
    reportWidgets: Widget[] = [],
) {
    // Get the actual DOM element from the React ref
    const container = gridRef.current;
    // If the container doesn't exist, exit early (nothing to export)
    if (!container) return;

    // Image quality multiplier: 1.5 = 1.5x resolution (balance between quality and file size)
    // Lower = smaller file, less quality. Higher = larger file, better quality
    const scale = options.scale ?? 1.5;

    // CSS selector to find all widgets in the report (each widget has data-report-widget-id attribute)
    const widgetSelector = options.widgetSelector ?? '[data-report-widget-id]';

    // ============== HEADER CONFIGURATION ==============
    const headerHeight = 10; // Height reserved for header at top of each page (in millimeters)
    const footerHeight = 10; // Height reserved for footer at bottom of each page (in millimeters)
    const headerText = 'Report Header'; // Text to display in header
    const footerText = 'Confidential'; // Text to display in footer
    const headerFontSize = 12; // Font size for header text (in points)
    const footerFontSize = 9; // Font size for footer text (in points)

    // ============== LOGO CONFIGURATION ==============
    const logoSize = 8; // Size for logo width and height (in millimeters)
    const foresightLogoFooterWidth = 30; // Width of Foresight footer logo (in millimeters)
    const foresightLogoFooterHeight = 5; // Height of Foresight footer logo (in millimeters)
    const footerLineColor = [0, 0, 0] as const; // RGB values for black line
    const footerTextGray = [102, 102, 102] as const; // RGB for gray text (#666666)
    const footerOrangeColor = [255, 102, 0] as const; // RGB for orange link (#FF6600)

    // ============== MARGINS CONFIGURATION ==============
    const marginLeft = 5; // Space from left edge of page (in millimeters)
    const marginRight = 5; // Space from right edge of page (in millimeters)
    const marginTop = 5; // Extra space between header and content (in millimeters)
    const marginBottom = 5; // Extra space between content and footer (in millimeters)

    // ============== CONTENT SCALING ==============
    // Percentage of available width to use for content (0.9 = 90% = smaller widgets = more fit per page)
    // 1.0 = full width, 0.8 = 80% width (even smaller), etc.
    const contentScale = 0.9;

    // ============== STEP 1: MEASURE WIDGET POSITIONS ==============
    // Get the position and size of the container element on the screen
    const containerRect = container.getBoundingClientRect();

    // Find all widgets inside the container using the selector
    // Array.from converts the NodeList to a regular array
    const widgetEls = Array.from(container.querySelectorAll<HTMLElement>(widgetSelector));

    // Calculate each widget's position and create an array of bounds
    const widgetBounds = widgetEls
        .map((el) => {
            // Get this widget's position and size relative to the viewport
            const rect = el.getBoundingClientRect();

            // Calculate position relative to container top (not viewport)
            // Multiply by scale because html2canvas will render at higher resolution
            const top = (rect.top - containerRect.top) * scale;
            const bottom = (rect.bottom - containerRect.top) * scale;

            // Return object with widget's vertical position in canvas pixels
            return { top, bottom, height: bottom - top };
        })
        // CRITICAL: Sort widgets from top to bottom
        // This ensures we check widgets in order when finding page breaks
        .sort((a, b) => a.top - b.top);

    // ============== STEP 2: CONVERT HTML TO IMAGE ==============
    options.onProgress?.(25, 'Capturing report content...');

    // html2canvas takes a screenshot of the entire report container
    // Returns a Canvas element (like a bitmap image) containing the screenshot
    const canvas = await html2canvas(container, {
        scale, // Resolution multiplier (1.5x means 1.5 times the pixels)
        useCORS: true, // Allow loading images from other domains (for charts, icons, etc.)
        scrollY: -window.scrollY, // Compensate if page is scrolled down
    });

    // ============== STEP 3: CREATE PDF DOCUMENT ==============
    options.onProgress?.(45, 'Creating PDF document...');

    // Create a new PDF in landscape orientation, using millimeters, A4 size
    const pdf = new jsPDF('landscape', 'mm', 'a4');

    // ============== STEP 3.5: CREATE COVER PAGE ==============
    options.onProgress?.(50, 'Creating cover page...');
    createCoverPage(pdf, reportName, reportWidgets, logoDataUrl, foresightLogoFooterDataUrl);

    // Add a new page for content
    pdf.addPage('a4', 'landscape');

    // Get the dimensions of an A4 page in landscape mode
    // A4 landscape = 297mm wide x 210mm tall
    const pageWidth = pdf.internal.pageSize.getWidth(); // 297mm
    const pageHeight = pdf.internal.pageSize.getHeight(); // 210mm

    // ============== STEP 4: CALCULATE AVAILABLE SPACE ==============
    // Subtract margins from page width to get space available for content
    const availableWidth = pageWidth - marginLeft - marginRight;

    // Subtract header, footer, and top/bottom margins from page height
    const availableHeight = pageHeight - headerHeight - footerHeight - marginTop - marginBottom;

    // ============== STEP 5: CALCULATE SCALING ==============
    // Scale content width down by contentScale factor (e.g., 0.9 = 90% of available width)
    const imgWidth = availableWidth * contentScale;

    // Calculate proportional height to maintain aspect ratio
    // Formula: newHeight = (canvasHeight / canvasWidth) * newWidth
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // Calculate conversion ratio: how many canvas pixels equal 1mm in PDF
    const pxPerMm = canvas.height / imgHeight;

    // Calculate how many canvas pixels fit in the available height of one page
    const pageHeightPx = availableHeight * pxPerMm;

    // ============== STEP 6: INITIALIZE PAGINATION VARIABLES ==============
    let currentY = 0; // Track current vertical position in canvas (in pixels)
    let pageIndex = 0; // Track which page number we're on (0 = first page)

    // ============== STEP 7: LOOP THROUGH CANVAS AND CREATE PAGES ==============
    options.onProgress?.(55, 'Generating pages...');

    // Keep creating pages until we've processed the entire canvas height
    while (currentY < canvas.height) {
        // Calculate where this page would naturally end (current position + page height)
        const targetEnd = currentY + pageHeightPx;

        // Check if this is the last page (target end reaches or exceeds canvas height)
        const isLastPage = targetEnd >= canvas.height;

        // Variable to store where we'll actually slice this page
        let sliceEnd: number;

        if (isLastPage) {
            // LAST PAGE: Just go all the way to the bottom of the canvas
            sliceEnd = canvas.height;
        } else {
            // NOT LAST PAGE: Need to find a smart place to break that doesn't cut widgets

            // Start with the natural page break position
            sliceEnd = targetEnd;

            // Check each widget (in order from top to bottom) to see if it would be cut
            for (const widget of widgetBounds) {
                // Check if this widget spans across the page boundary
                // widget.top < targetEnd = widget starts above the break
                // widget.bottom > targetEnd = widget ends below the break
                // Both true = widget is split by the break = BAD!
                if (widget.top < targetEnd && widget.bottom > targetEnd) {
                    // This widget would be cut! Move the page break to BEFORE this widget
                    // This pushes the widget entirely to the next page
                    sliceEnd = widget.top;

                    // Stop checking - we found the problematic widget
                    break;
                }
            }

            // SAFETY CHECK: Make sure we're making progress (not stuck in infinite loop)
            if (sliceEnd <= currentY) {
                // If sliceEnd didn't advance, we have a problem
                // This could happen if a widget is taller than a page

                // Find the next widget that starts at or after current position
                const nextWidget = widgetBounds.find((w) => w.top >= currentY);

                if (nextWidget) {
                    // Include this entire widget even if it's too tall
                    sliceEnd = nextWidget.bottom;
                } else {
                    // No widgets found, just use natural break
                    sliceEnd = Math.min(targetEnd, canvas.height);
                }
            }

            // FINAL SAFETY: Ensure sliceEnd doesn't exceed canvas height
            sliceEnd = Math.min(sliceEnd, canvas.height);
        }

        // Calculate height of this slice in canvas pixels
        const sliceHeightPx = sliceEnd - currentY;

        // If slice has no height, something went wrong - exit the loop
        if (sliceHeightPx <= 0) break;

        // ============== STEP 8: CREATE A CANVAS FOR THIS PAGE ==============
        // Create a new empty canvas element to hold just this page's content
        const pageCanvas = document.createElement('canvas');

        // Set width to match full canvas width
        pageCanvas.width = canvas.width;

        // Set height to the calculated slice height for this page
        pageCanvas.height = sliceHeightPx;

        // Get the 2D drawing context (the "pen" to draw on the canvas)
        const ctx = pageCanvas.getContext('2d');

        // If context is null (extremely rare), exit - can't draw without it
        if (!ctx) break;

        // Copy a slice of the main canvas onto this page canvas
        ctx.drawImage(
            canvas, // Source: the full report screenshot
            0, // Source X: start from left edge
            currentY, // Source Y: start from current vertical position
            canvas.width, // Source Width: copy full width
            sliceHeightPx, // Source Height: copy this much height
            0, // Destination X: paste at left edge of new canvas
            0, // Destination Y: paste at top edge of new canvas
            canvas.width, // Destination Width: paste at full width
            sliceHeightPx, // Destination Height: paste at same height
        );

        // Convert this page's canvas to a JPEG image with 85% quality
        // toDataURL returns a base64 encoded image string
        const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.85);

        // Convert slice height from canvas pixels to PDF millimeters
        const sliceHeightMm = sliceHeightPx / pxPerMm;

        // ============== STEP 9: ADD NEW PAGE TO PDF (if not first page) ==============
        if (pageIndex > 0) {
            // Add a new blank page to the PDF (landscape A4)
            pdf.addPage('a4', 'landscape');
        }

        // ============== STEP 10: DRAW HEADER ==============
        // Draw client logo in top left corner (if available)
        if (logoDataUrl) {
            pdf.addImage(logoDataUrl, 'PNG', marginLeft, marginTop, logoSize, logoSize);
        }

        // ============== STEP 11: DRAW CONTENT IMAGE ==============
        // Calculate X position to center content horizontally
        // Start from left margin, add half of remaining space
        const contentX = marginLeft + (availableWidth - imgWidth) / 2;

        // Calculate Y position to center content vertically
        // Start after header + top margin, add half of remaining space
        const contentY = headerHeight + marginTop + (availableHeight - sliceHeightMm) / 2;

        // Add the page image to the PDF at calculated position and size
        pdf.addImage(pageImgData, 'JPEG', contentX, contentY, imgWidth, sliceHeightMm);

        // ============== STEP 12: DRAW FOOTER ==============
        // Calculate footer position (at bottom of page)
        const footerLineY = pageHeight - footerHeight - marginBottom;

        // Draw black horizontal line above the footer
        pdf.setDrawColor(...footerLineColor);
        pdf.setLineWidth(0.3); // Line thickness in millimeters
        pdf.line(marginLeft, footerLineY, pageWidth - marginRight, footerLineY);

        // "Powered by" text (left side, above logo)
        const poweredByY = footerLineY + 4;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Powered by', marginLeft, poweredByY);

        // Draw Foresight footer logo below "Powered by" text
        if (foresightLogoFooterDataUrl) {
            const logoY = footerLineY + 5.5;
            pdf.addImage(
                foresightLogoFooterDataUrl,
                'PNG',
                marginLeft,
                logoY,
                foresightLogoFooterWidth,
                foresightLogoFooterHeight,
            );
        }

        // Center text with hyperlink: "Dive into more details in Foresight platform (login required)"
        const centerTextY = footerLineY + 8;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);

        // Calculate text positions for alignment
        const beforeText = 'Dive into more details in ';
        const linkText = 'Foresight platform';
        const afterText = ' (login required).';

        // Measure text widths
        const beforeWidth = pdf.getTextWidth(beforeText);
        const linkWidth = pdf.getTextWidth(linkText);
        const afterWidth = pdf.getTextWidth(afterText);
        const totalWidth = beforeWidth + linkWidth + afterWidth;

        // Calculate starting X position for center alignment
        const startX = (pageWidth - totalWidth) / 2;

        // Draw "Dive into more details in " (gray)
        pdf.setTextColor(...footerTextGray);
        pdf.text(beforeText, startX, centerTextY);

        // Draw "Foresight platform" (orange with link)
        const linkX = startX + beforeWidth;
        pdf.setTextColor(...footerOrangeColor);
        pdf.textWithLink(linkText, linkX, centerTextY, { url: window.location.href });

        // Draw " (login required)." (gray)
        const afterX = linkX + linkWidth;
        pdf.setTextColor(...footerTextGray);
        pdf.text(afterText, afterX, centerTextY);

        // Draw page number on the right side (black color)
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        const pageNumY = footerLineY + 8;
        pdf.text(String(pageIndex + 1), pageWidth - marginRight, pageNumY, { align: 'right' });

        // ============== STEP 13: MOVE TO NEXT PAGE ==============
        // Update current Y position to where this page ended
        currentY = sliceEnd;

        // Increment page counter for next iteration
        pageIndex++;
    }

    // ============== STEP 14: SAVE PDF FILE ==============
    options.onProgress?.(90, 'Saving PDF file...');

    // Trigger browser download with filename (default: 'report.pdf')
    const dateTimeSuffix = `${moment(new Date()).format(constants.formats.date.yearMonthDay)}_${moment(new Date()).format('HHmm')}`;
    const defaultFileName = `report_${dateTimeSuffix}.pdf`;
    const fileName = options.fileName
        ? `${options.fileName.replace(/\.pdf$/i, '')}_${dateTimeSuffix}.pdf`
        : defaultFileName;
    pdf.save(fileName);
}
